import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, role } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated && role === 'admin') {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, role, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    try {
      const response = await authAPI.staffLogin(email, password);
      if (response.data.role !== 'admin') {
        toast.error('Admin access required');
        return;
      }
      login(response.data.token, response.data.user, response.data.role);
      toast.success('Welcome back, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-plum flex flex-col">
      <header className="px-6 py-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md animate-fadeIn">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
              <Shield className="w-8 h-8 text-[#C9A689]" />
            </div>
            <h1 className="font-serif text-4xl text-white mb-2">Salon O</h1>
            <p className="text-[#C9A689] tracking-widest text-sm uppercase">Admin Portal</p>
          </div>

          <form onSubmit={handleSubmit} className="glass-dark rounded-2xl p-8">
            <div className="space-y-6">
              <div className="form-group">
                <label htmlFor="email" className="form-label text-white/80">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@salono.com"
                    className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label text-white/80">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 bg-[#C9A689] hover:bg-[#b89878] text-white font-medium"
                data-testid="login-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Signing in...</>
                ) : (
                  'Sign In'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
